import { ConfirmationDialog, FlatIconButton, Icon, Id, RadioControl, TextButton, TextField, Trigger } from "@dgs/core";
import { FieldArray, useField } from "formik";
import { TFunction } from "i18next";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	ContactAttributeRequest,
	IContactAttributeBaseHandler,
	IContactAttributeHandler,
	IContactAttributeListHandler,
} from "../IContactAttributeHandler";
import { RelationListRow, RelationListSection, TwoColumn } from "./RelationListSection";
import { usePrimaryRelationFieldArray } from "./usePrimaryRelationFieldArray";

interface CompanyValue {
	company: string;
	position: string | null;
	isPrimary: boolean;
}

const emptyValue: CompanyValue = { company: "", position: null, isPrimary: false };

const isEmpty = (value: CompanyValue) => value.company === "" && value.position === null;

export function createCompaniesContactAttributeBaseHandler(id: Id): IContactAttributeBaseHandler {
	return {
		type: "COMPANIES",
		id,
		getLabel: (t) => t("Companies"),
	};
}

export function createCompaniesContactAttributeProfileHandler(
	id: Id,
	value: CompanyValue[],
): IContactAttributeHandler<CompanyValue[]> {
	const baseHandler = createCompaniesContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		value: value.length === 0 ? [emptyValue] : value,
		validate(t, values) {
			const errors: Record<number, any> = {};
			let hasPrimary = false;
			let hasNonEmptyValues = false;

			values.forEach((company, index) => {
				if (isEmpty(company)) return;
				hasPrimary ||= company.isPrimary;
				hasNonEmptyValues = true;

				if (!company.company) {
					errors[index] = { company: t("Field is required") };
				}
			});

			if (!hasPrimary && hasNonEmptyValues) {
				return t("At least one company must be marked as primary");
			}
			return errors;
		},
		renderValue(): ReactNode {
			return value.map(({ company }) => company).join(", ");
		},
		renderFormField(_: TFunction, prefix): ReactNode {
			const name = `${prefix ? `${prefix}.` : ""}${id}.value`;
			return <FormField name={name} />;
		},
		toRequestValue(): ContactAttributeRequest<CompanyValue[]> {
			return { id, value: this.value.filter((company) => !isEmpty(company)) };
		},
	};
}

export function createCompaniesContactAttributeListHandler(id: Id): IContactAttributeListHandler {
	const baseHandler = createCompaniesContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		getColumnSize: () => "1fr",
	};
}

const FormField: FC<{ name: string }> = ({ name }) => {
	const { t } = useTranslation();
	const { items, replace, push, remove } = usePrimaryRelationFieldArray<CompanyValue>(name, emptyValue);
	const [, { error }] = useField(name);
	const hasOnlyOneEmptyItem = items.length === 1 && isEmpty(items[0]);

	return (
		<FieldArray name={name}>
			{() => (
				<RelationListSection title={t("Companies")} error={typeof error === "string" ? error : undefined}>
					{items.map((company, index) => (
						<RelationListRow key={index}>
							<TwoColumn>
								<TextField name={`${name}.${index}.company`} label={t("Company")} required />
								<TextField name={`${name}.${index}.position`} label={t("Position")} />
								<RadioControl
									id={`company-${index}-primary`}
									name="comapanies.isPrimary"
									value={String(index)}
									checked={company.isPrimary}
									label={t("Mark as primary")}
									onChange={(e) => replace(index, { ...company, isPrimary: e.target.value === String(index) })}
								/>
							</TwoColumn>
							<Trigger
								render={(dialogProps) => (
									<ConfirmationDialog
										heading={t("Delete company")}
										labels={{ confirm: t("Delete"), close: t("Cancel") }}
										open={dialogProps.open}
										onClose={dialogProps.handleClose}
										action={(close) => {
											remove(index);
											close();
										}}
									>
										{t("Are you sure you want to delete this company?")}
									</ConfirmationDialog>
								)}
							>
								{(triggerProps) => (
									<FlatIconButton
										{...triggerProps}
										type="button"
										title={t("Delete")}
										icon="delete"
										disabled={hasOnlyOneEmptyItem}
									/>
								)}
							</Trigger>
						</RelationListRow>
					))}
					<span>
						<TextButton onClick={() => push(emptyValue)} type="button" prefix={<Icon $icon="plus" />}>
							{t("Add company")}
						</TextButton>
					</span>
				</RelationListSection>
			)}
		</FieldArray>
	);
};
